import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Container, Grid, Skeleton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { styles } from './styles';
import { useNavigate, useParams } from 'react-router-dom';
import { ModifyRule } from './ModifyRule';
import { AddRule } from './AddRule';
import { PolicyStatusColor } from './columns';
import AuditLog from '../../../components/common/AuditLog';
import { RecruitmentPolicyMenu } from './RecruitmentPolicyMenu';
import ActionButton from '../../../components/common/ActionButtonAndMenu/ActionButton';
import AuditLogTable from '../../../components/common/AuditLog/AuditLogTable';
import { usePreCreateRulesForRecruitmentPolicyMutation } from '../../../api/recruitmentPolicy';
import { usePreCreatePositionMutation } from '../../../api/positions';
import { useSnackbar } from 'notistack';

export const initialFieldDefinitions = {
    1: {
        id: 1,
        fieldLabel: 'Designation',
        fieldDataType: 'Single Selection List',
        fieldListOptionsData: {
            key: 'designationsList',
            id: 'id',
            value: 'name',
        },
    },
    2: {
        id: 2,
        fieldLabel: 'Budget Lower Limit',
        fieldDataType: 'String',
    },
    3: {
        id: 3,
        fieldLabel: 'Budget Upper Limit',
        fieldDataType: 'String',
    },
    4: {
        id: 4,
        fieldLabel: 'Budget Mid Limit',
        fieldDataType: 'String',
    },
    5: {
        id: 5,
        fieldLabel: 'Department',
        fieldDataType: 'Single Selection List',
        fieldListOptionsData: {
            key: 'departmentsList',
            id: 'id',
            value: 'name',
        },
    },
    6: {
        id: 6,
        fieldLabel: 'Location',
        fieldDataType: 'Single Selection List',
        fieldListOptionsData: {
            key: 'locationsList',
            id: 'id',
            value: 'name',
        },
    },
    7: {
        id: 7,
        fieldLabel: 'Position Code',
        fieldDataType: 'String',
    },
    8: {
        id: 8,
        fieldLabel: 'Position Status',
        fieldDataType: 'String',
    },
    9: {
        id: 9,
        fieldLabel: 'Position Start Date',
        fieldDataType: 'Date',
    },
};

export const ViewRecruitmentPolicy = () => {
    const [data, setData] = useState({});
    const [action, setAction] = useState('view');
    const [modifyId, setModifyId] = useState();
    const navigate = useNavigate();
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const [preCreateRulesForRecruitmentPolicy, { isLoading: isPreCreateRulesForRecruitmentPolicyLoading }] =
        usePreCreateRulesForRecruitmentPolicyMutation();
    const [preCreatePosition, { isLoading: isPreCreatePositionLoading }] = usePreCreatePositionMutation();

    useEffect(() => {
        action === 'add' && window.scrollTo(0, document.body.scrollHeight);
    }, [action]);

    const fetchData = useCallback(async () => {
        try {
            const bodyFormData = new FormData();
            bodyFormData.append('recruitmentPolicyId', params.id);

            const prePositionData = await preCreatePosition().unwrap();
            if (!prePositionData) return;
            if (prePositionData?.status === 'ERROR') throw new Error(prePositionData?.message);

            let definitions = JSON.parse(JSON.stringify(initialFieldDefinitions));
            Object.keys(definitions)?.map((item) => {
                if (
                    definitions[item].fieldDataType === 'Single Selection List' ||
                    definitions[item].fieldDataType === 'Multiple Selection List'
                ) {
                    const fieldListOptionsData = definitions[item]['fieldListOptionsData'];
                    const listOptions = prePositionData[fieldListOptionsData?.key]?.map((item) => ({
                        key: item[fieldListOptionsData?.id],
                        label: item[fieldListOptionsData?.value],
                    }));
                    definitions[item]['fieldListOptions'] = listOptions || [];
                }
            });

            if (prePositionData?.extendedDef?.metaCustomFieldDefinitions) {
                prePositionData?.extendedDef?.metaCustomFieldDefinitions?.map((item) => {
                    definitions[item.id] = {
                        id: item.id,
                        fieldLabel: item.fieldLabel,
                        fieldDataType: item.fieldDataType,
                        fieldListOptions: item.fieldListOptions,
                    };
                });
            }

            const preRuleData = await preCreateRulesForRecruitmentPolicy(bodyFormData).unwrap();
            if (!preRuleData) return;
            if (preRuleData?.status === 'ERROR') throw new Error(preRuleData?.message);

            const attributes = preRuleData?.attributes.map((item) => ({
                ...item,
                definitions: definitions[item.id],
            }));

            setData({
                ...preRuleData,
                attributes,
            });
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    }, [params.id, preCreateRulesForRecruitmentPolicy, preCreatePosition, enqueueSnackbar]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleBack = () => {
        navigate('/recruitmentPolicies');
    };

    const handleRefresh = () => {
        fetchData();
        setAction('view');
    };

    const handleModify = (id) => {
        setAction('modify');
        setModifyId(id);
    };

    return (
        <>
            <Box sx={styles.growBox}>
                <Container>
                    <Box
                        sx={{
                            pl: '8px',
                            pt: '12px !important',
                            pb: '32px',
                        }}
                    >
                        <Box>
                            <Typography variant="h4">Recruitment Policy</Typography>
                        </Box>
                    </Box>
                    <Card
                        sx={{
                            ...styles.dataWrappper,
                            pt: 0,
                            mt: 0,
                        }}
                    >
                        <CardContent>
                            <>
                                <Grid container direction="column" spacing={2}>
                                    <Grid
                                        item
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Button
                                            startIcon={<ChevronLeftIcon />}
                                            variant="text"
                                            color="secondary"
                                            onClick={handleBack}
                                        >
                                            Back
                                        </Button>
                                        <RecruitmentPolicyMenu
                                            Component={ActionButton}
                                            page="view"
                                            setRuleAction={setAction}
                                            row={{
                                                id: params.id,
                                                ...data,
                                            }}
                                            handleAction={() => navigate('/recruitmentPolicies')}
                                        />
                                    </Grid>
                                    <Grid item marginX={4}>
                                        <Box>
                                            <Typography variant="h5">Recruitment Policy Details</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item marginX={4}>
                                        {isPreCreatePositionLoading ? (
                                            <Grid item marginX={4}>
                                                <Skeleton height={42} />
                                                <Skeleton height={42} />
                                                <Skeleton height={42} />
                                            </Grid>
                                        ) : (
                                            <Grid container spacing={3}>
                                                <Grid item xs={4}>
                                                    <Box sx={styles.dataItem}>
                                                        <Typography component={'span'} sx={styles.dataItemLable}>
                                                            Name
                                                        </Typography>
                                                        <Typography component={'span'} sx={styles.dataItemValue}>
                                                            {data?.recruitmentPolicyName}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Box sx={styles.dataItem}>
                                                        <Typography component={'span'} sx={styles.dataItemLable}>
                                                            Status
                                                        </Typography>
                                                        <Box>
                                                            <Chip
                                                                label={data?.recruitmentPolicyStatus}
                                                                sx={{
                                                                    backgroundColor: PolicyStatusColor(
                                                                        data?.recruitmentPolicyStatus,
                                                                    ),
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        )}
                                        {isPreCreatePositionLoading || isPreCreateRulesForRecruitmentPolicyLoading ? (
                                            <Grid item marginX={4} marginY={4}>
                                                <Skeleton height={42} />
                                                <Skeleton height={42} />
                                                <Skeleton height={42} />
                                            </Grid>
                                        ) : (
                                            <Grid>
                                                {data?.attributes?.length > 0 ? (
                                                    data?.rules?.length > 0 || action === 'add' ? (
                                                        data?.rules?.map((item, index) => (
                                                            <ModifyRule
                                                                key={item.id}
                                                                index={index}
                                                                rule={item}
                                                                data={data}
                                                                modifyId={modifyId}
                                                                handleModify={handleModify}
                                                                handleRefresh={handleRefresh}
                                                            />
                                                        ))
                                                    ) : (
                                                        <Box py="20px" textAlign="center">
                                                            No rules found
                                                        </Box>
                                                    )
                                                ) : (
                                                    action !== 'add' && (
                                                        <Box py="20px" textAlign="center">
                                                            No rule attributes found
                                                        </Box>
                                                    )
                                                )}
                                                {action === 'add' && (
                                                    <AddRule
                                                        data={data}
                                                        handleCancel={() => setAction('view')}
                                                        handleRefresh={handleRefresh}
                                                    />
                                                )}
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                                <Box mt={3} mx={2}>
                                    <AuditLog
                                        data={[
                                            {
                                                title: 'Audit Logs',
                                                logs: data?.commentsList || [],
                                                Component: AuditLogTable,
                                            },
                                        ]}
                                    />
                                </Box>
                            </>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        </>
    );
};
